import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { setApiTenantId } from '../api';
import { applyThemeColors } from '../config/theme';
import { monthlyPriceFor, formatPrice } from '../config/pricing';
import { updateMetaTags } from '../utils/updateMetaTags';

// Helper to detect tenant from hostname
// Returns: { type: 'subdomain', value: 'greenacres' } or { type: 'custom_domain', value: 'greenacresland.com' } or null for marketing site
// The Auction Calendar is a separate product, sold on its own, served by this
// same Netlify site through hostname routing exactly as the tenant storefronts
// are. calendar.deedstack.com is the testing alias for calendar.yourfirstparcel.com.
export const CALENDAR_HOSTNAMES = Object.freeze([
  'calendar.yourfirstparcel.com',
  'calendar.deedstack.com'
]);

export const isCalendarHostname = (hostname) => {
  if (!hostname) return false;
  return CALENDAR_HOSTNAMES.includes(String(hostname).toLowerCase().split(':')[0]);
};

export const detectTenantFromHostname = (hostname) => {
  console.log('Detecting tenant for hostname:', hostname);

  // Handle localhost and local development
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost:')) {
    console.log('Localhost detected - marketing site');
    return null;
  }

  // EXPLICIT: Main marketing domain (no subdomain)
  if (hostname === 'deedstack.com' || hostname === 'www.deedstack.com') {
    console.log('Main deedstack.com domain detected - marketing site');
    return null;
  }

  // The Auction Calendar, which MUST be tested before the two branches below.
  // calendar.deedstack.com matches the subdomain regex and would be looked up as
  // a tenant named "calendar"; calendar.yourfirstparcel.com falls through to the
  // custom domain branch, which would call /tenant/by-domain, get a 404, and
  // render "Site Not Found" over the whole product.
  if (isCalendarHostname(hostname)) {
    console.log('Auction Calendar hostname detected - not a tenant site');
    return null;
  }

  // Handle Railway production URL
  if (hostname.includes('railway.app')) {
    console.log('Railway domain detected - marketing site');
    return null;
  }

  // Check for deedstack.com subdomains (e.g., demo.deedstack.com)
  const deedstackMatch = hostname.match(/^([^.]+)\.deedstack\.com$/i);
  if (deedstackMatch) {
    const subdomain = deedstackMatch[1].toLowerCase();
    console.log('Subdomain extracted:', subdomain);
    // Exclude www and other reserved subdomains
    if (subdomain === 'www' || subdomain === 'api' || subdomain === 'admin') {
      console.log('Reserved subdomain - treating as marketing site');
      return null;
    }
    console.log('Valid tenant subdomain:', subdomain);
    return { type: 'subdomain', value: subdomain };
  }

  // Handle Netlify preview URLs (e.g., deploy-preview-123--deedstack.netlify.app)
  const netlifyMatch = hostname.match(/^([^.]+)\.netlify\.app$/i);
  if (netlifyMatch) {
    console.log('Netlify preview detected - marketing site');
    return null;
  }

  // If we get here, it might be a custom domain
  // Check if it looks like a valid domain (has at least one dot, not an IP)
  const isIPAddress = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname);
  if (!isIPAddress && hostname.includes('.')) {
    // This could be a custom domain like "greenacreslandinvestments.com"
    const cleanDomain = hostname.toLowerCase().split(':')[0]; // Remove port if present
    console.log('Potential custom domain detected:', cleanDomain);
    return { type: 'custom_domain', value: cleanDomain };
  }

  console.log('No tenant match - defaulting to marketing site');
  return null;
};

// Legacy helper for backwards compatibility
export const getSubdomainFromHostname = (hostname) => {
  const result = detectTenantFromHostname(hostname);
  if (result && result.type === 'subdomain') {
    return result.value;
  }
  return null;
};

// Subscription tier constants
const TIERS = {
  FREE_TRIAL: 'free_trial',
  STARTER: 'starter',
  PRO: 'pro',
  ENTERPRISE: 'enterprise'
};

// DeedStack default logo paths
// Note: light version uses the same logo as horizontal (it's already designed for dark backgrounds)
const DEEDSTACK_LOGOS = {
  horizontal: '/images/deedstack-logos/logo-horizontal-final.svg',
  icon: '/images/deedstack-logos/logo-icon-final.svg',
  full: '/images/deedstack-logos/logo-horizontal-final.svg',
  light: '/images/deedstack-logos/logo-horizontal-final.svg' // Same as horizontal - works on dark backgrounds
};

// Default config values (used while loading or if fetch fails)
const defaultConfig = {
  branding: {
    companyName: 'DeedStack™ Demo',
    companyLegalName: 'DeedStack Demo LLC',
    companyAddress: '123 Main St, City, ST 12345',
    companyPhone: '(555) 555-5555',
    companyEmail: 'info@example.com',
    tagline: 'Land Financing, Simplified',
    footerText: '© 2025 DeedStack Demo. All rights reserved.',
    websiteUrl: 'https://example.com',
    // The storefront's starting theme for a visitor who has never chosen one and
    // has no account. Set per tenant in Branding Settings; 'system' follows the
    // visitor's own device. Published inside branding by the three site
    // endpoints, so it arrives with the rest of the tenant's identity.
    defaultTheme: 'system',
    logos: {
      horizontal: '/images/deedstack-logos/logo-horizontal-final.svg',
      icon: '/images/deedstack-logos/logo-icon-final.svg',
      full: '/images/deedstack-logos/logo-horizontal-final.svg',
      light: null,
      // The icon index.html actually links. /images/favicon.ico has never
      // existed in client/public, so this fallback resolved to a 404.
      favicon: '/images/deedstack-logos/logo-icon-final.svg'
    }
  },
  colors: {
    primaryColor: '#1a365d',
    primaryDark: '#122442',
    primaryLight: '#2a4a7d',
    secondaryColor: '#c9a227',
    secondaryDark: '#a68617',
    secondaryLight: '#d4af37',
    accentColor: '#c9a227',
    successColor: '#28a745',
    warningColor: '#ffc107',
    dangerColor: '#dc3545',
    infoColor: '#17a2b8',
    lightBackground: '#f0f4f8',
    // Literal on purpose, like the rest of this block. These values are the
    // SOURCE the theme variables are built from, so writing var(--color-border)
    // here would define that variable in terms of itself and leave it unset.
    borderColor: '#e0e0e0'
  },
  features: {
    enableEbayIntegration: true,
    enableHoaTracking: true,
    enableTaxEscrow: true,
    enableEscrowAlerts: true,
    enablePropertyCovenants: true,
    enablePropertySources: true,
    enableAuctionCalendar: true,
    enableSoldShowcase: true,
    enableEmailNotifications: true,
    enablePaymentReminders: true,
    enableContracts: true,
    enablePdfExports: true,
    enableAccountSettings: true,
    enableMultiState: true
  },
  // ==================== LAST-RESORT FALLBACK ONLY ====================
  // NOT the normal path. On any tenant site these values are replaced wholesale
  // by the businessSettings block published by /api/tenant/by-subdomain or
  // /api/tenant/by-domain, and on the marketing site by /api/config/public.
  // This literal is reached only when there is genuinely no tenant context and
  // no server response -- initial render before the fetch resolves, or a failed
  // config request.
  //
  // If you see these exact numbers on a live tenant site ($75 late fee, 7-day
  // grace), the settings did not reach the browser; that is a bug in the
  // publishing chain, not something to fix by editing the numbers here.
  //
  // Every key here MUST also be published by publishedBusinessSettings() in
  // server/server.js. `npm run check:settings` (run from server/) enforces that
  // and fails if the two lists drift apart.
  businessSettings: {
    // 'offered' = the buyer picks from paymentDueDayOptions; 'anniversary' = the
    // due day is the day of the month the loan closes and no picker is shown.
    // Mirrors siteConfig.BUSINESS_SETTINGS.paymentDueDayMode on the server.
    paymentDueDayMode: 'offered',
    paymentDueDayOptions: [1, 15],
    convenienceFee: 5,
    minimumMonthlyPayment: 50,
    // Empty on purpose, and it must stay empty. Everything else in this literal
    // is a neutral platform convention; a financing tier is one specific
    // business's price. Falling back to a real-looking tier here would quote a
    // buyer a down payment and an APR that their seller never agreed to, on the
    // exact code path that runs when the tenant's real settings failed to
    // arrive. An empty list renders no options instead of wrong ones.
    // Mirrors siteConfig.BUSINESS_SETTINGS.financingTiers on the server.
    financingTiers: [],
    termOptions: [1, 2, 3, 4, 5],
    propertyLimits: { minPropertyPrice: 2000, maxPropertyPrice: 50000 },
    lateFeeAmount: 75,
    noticeFeeAmount: 75,
    processingFee: 99,
    gracePeriodDays: 7,
    curePeriodDays: 14,
    noticeEligibilityDays: 30,
    escrowAlertDays: 30,
    escrowRepriceMinMonths: 12,
    returnedPaymentFee: 75,
    changeDueDateFee: 75,
    propertyTransferFee: 175,
    lienReleaseFee: 75,
    ccProcessingRate: 2.9,
    ccPerTransactionFee: 0.30
  }
};

const ConfigContext = createContext(defaultConfig);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentTenant, setCurrentTenant] = useState(null);
  const [isMarketingSite, setIsMarketingSite] = useState(false);
  const [subdomain, setSubdomain] = useState(null);

  useEffect(() => {
    const initializeConfig = async () => {
      try {
        // Detect tenant from current hostname (subdomain or custom domain)
        const hostname = window.location.hostname;
        const tenantDetection = detectTenantFromHostname(hostname);
        const isMarketing = !tenantDetection;

        console.log('[Config] Hostname:', hostname, 'Detection:', tenantDetection, 'Is Marketing:', isMarketing);

        // For backwards compatibility, set subdomain if detected
        if (tenantDetection?.type === 'subdomain') {
          setSubdomain(tenantDetection.value);
        }

        if (tenantDetection) {
          // Tenant detected (either subdomain or custom domain)
          console.log(`Tenant detected: ${tenantDetection.type} = ${tenantDetection.value}`);
          try {
            // Call the appropriate endpoint based on detection type
            const endpoint = tenantDetection.type === 'subdomain'
              ? `/tenant/by-subdomain/${tenantDetection.value}`
              : `/tenant/by-domain/${encodeURIComponent(tenantDetection.value)}`;

            console.log(`[Config] Fetching tenant config from: ${endpoint}`);
            const tenantResponse = await api.get(endpoint);
            const tenantData = tenantResponse.data;

            // IMMEDIATELY update meta tags before React state updates
            // This runs synchronously to update favicon/title before first paint
            updateMetaTags({
              companyName: tenantData.tenant?.companyName,
              tagline: tenantData.branding?.tagline,
              metaDescription: tenantData.branding?.metaDescription,
              logos: tenantData.branding?.logos,
              primaryColor: tenantData.colors?.primaryColor
            }, false);

            // Set tenant info
            setCurrentTenant(tenantData.tenant);

            // For custom domain, also set the subdomain from tenant data
            if (tenantDetection.type === 'custom_domain' && tenantData.tenant?.subdomain) {
              setSubdomain(tenantData.tenant.subdomain);
            }

            // Set tenant ID in API client for multi-tenant data isolation
            if (tenantData.tenant?.id) {
              setApiTenantId(tenantData.tenant.id);
            }

            // Build config from tenant data.
            //
            // businessSettings is taken WHOLESALE from the server, not spread
            // over defaultConfig. The server already resolved tenant -> global
            // -> siteConfig, so anything it publishes is authoritative and a
            // client-side default layered on top could only ever mask it.
            //
            // This block used to be omitted entirely, which left businessSettings
            // inherited from the hardcoded literal below -- every tenant's buyers
            // were quoted the platform's $75 late fee and 7-day grace period
            // regardless of what the tenant had configured.
            const tenantConfig = {
              ...defaultConfig,
              branding: {
                ...defaultConfig.branding,
                ...tenantData.branding
              },
              colors: {
                ...defaultConfig.colors,
                ...tenantData.colors
              },
              businessSettings: tenantData.businessSettings || defaultConfig.businessSettings,
              squareConfig: tenantData.squareConfig || null
            };

            setConfig(tenantConfig);
            setIsMarketingSite(false);

            // Apply tenant's theme colors
            if (tenantData.colors) {
              applyThemeColors(tenantData.colors);
            }

            console.log(`Loaded config for tenant: ${tenantData.tenant.companyName}`);
          } catch (tenantErr) {
            console.error('Failed to load tenant config:', tenantErr);
            // Tenant not found or error - show error state
            if (tenantErr.response?.status === 404) {
              setError('Site not found');
            } else if (tenantErr.response?.status === 403) {
              setError('This site is currently unavailable');
            } else {
              setError('Failed to load site configuration');
            }
            setIsMarketingSite(true);
          }
        } else {
          // No tenant detected - this is the marketing site or localhost
          console.log('No tenant detected - loading as marketing site');

          // Update meta tags to DeedStack defaults immediately
          updateMetaTags(null, true);

          setIsMarketingSite(true);

          // Load default/shared config for marketing site
          try {
            const response = await api.get('/config/public');
            setConfig(response.data);

            if (response.data.colors) {
              applyThemeColors(response.data.colors);
            }
          } catch (configErr) {
            console.error('Failed to fetch config:', configErr);
            // Keep using default config on error
          }
        }

        setError(null);
      } catch (err) {
        console.error('Config initialization error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeConfig();
  }, []);

  // Helper functions for common config access patterns
  const value = {
    ...config,
    loading,
    error,

    // Tenant-related properties
    currentTenant,
    isMarketingSite,
    subdomain,
    getTenantId: () => currentTenant?.id || null,
    getTenantName: () => currentTenant?.companyName || null,
    getTenantSubdomain: () => currentTenant?.subdomain || subdomain || null,
    isTenantSite: () => !!currentTenant,
    isDemoMode: () => subdomain === 'demo',

    // Convenience getters
    getCompanyName: () => config.branding?.companyName || defaultConfig.branding.companyName,
    getCompanyLegalName: () => config.branding?.companyLegalName || defaultConfig.branding.companyLegalName,
    getCompanyEmail: () => config.branding?.companyEmail || defaultConfig.branding.companyEmail,
    getCompanyPhone: () => config.branding?.companyPhone || defaultConfig.branding.companyPhone,
    getCompanyAddress: () => config.branding?.companyAddress || defaultConfig.branding.companyAddress,
    getTagline: () => config.branding?.tagline || defaultConfig.branding.tagline,
    getFooterText: () => config.branding?.footerText || defaultConfig.branding.footerText,
    getBusinessHours: () => config.branding?.businessHours || 'Monday - Friday, 9:00 AM - 5:00 PM CST',

    // Feature flags
    isFeatureEnabled: (featureName) => {
      return config.features?.[featureName] ?? defaultConfig.features[featureName] ?? false;
    },

    // Business settings
    getConvenienceFee: () => config.businessSettings?.convenienceFee ?? defaultConfig.businessSettings.convenienceFee,
    getPaymentDueDayMode: () => config.businessSettings?.paymentDueDayMode ?? defaultConfig.businessSettings.paymentDueDayMode,
    getPaymentDueDayOptions: () => config.businessSettings?.paymentDueDayOptions ?? defaultConfig.businessSettings.paymentDueDayOptions,
    getMinimumMonthlyPayment: () => config.businessSettings?.minimumMonthlyPayment ?? defaultConfig.businessSettings.minimumMonthlyPayment,
    getFinancingTiers: () => config.businessSettings?.financingTiers ?? defaultConfig.businessSettings.financingTiers,
    getTermOptions: () => config.businessSettings?.termOptions ?? defaultConfig.businessSettings.termOptions,
    getLateFee: () => config.businessSettings?.lateFeeAmount ?? defaultConfig.businessSettings.lateFeeAmount,
    getNoticeFee: () => config.businessSettings?.noticeFeeAmount ?? defaultConfig.businessSettings.noticeFeeAmount,
    getProcessingFee: () => config.businessSettings?.processingFee ?? defaultConfig.businessSettings.processingFee,
    getGracePeriodDays: () => config.businessSettings?.gracePeriodDays ?? defaultConfig.businessSettings.gracePeriodDays,
    getCurePeriodDays: () => config.businessSettings?.curePeriodDays ?? defaultConfig.businessSettings.curePeriodDays,
    getNoticeEligibilityDays: () => config.businessSettings?.noticeEligibilityDays ?? defaultConfig.businessSettings.noticeEligibilityDays,
    getEscrowAlertDays: () => config.businessSettings?.escrowAlertDays ?? defaultConfig.businessSettings.escrowAlertDays,
    getEscrowRepriceMinMonths: () => config.businessSettings?.escrowRepriceMinMonths ?? defaultConfig.businessSettings.escrowRepriceMinMonths,
    getPropertyLimits: () => config.businessSettings?.propertyLimits ?? defaultConfig.businessSettings.propertyLimits,
    getReturnedPaymentFee: () => config.businessSettings?.returnedPaymentFee ?? defaultConfig.businessSettings.returnedPaymentFee,
    getChangeDueDateFee: () => config.businessSettings?.changeDueDateFee ?? defaultConfig.businessSettings.changeDueDateFee,
    getPropertyTransferFee: () => config.businessSettings?.propertyTransferFee ?? defaultConfig.businessSettings.propertyTransferFee,
    getLienReleaseFee: () => config.businessSettings?.lienReleaseFee ?? defaultConfig.businessSettings.lienReleaseFee,
    getCcProcessingRate: () => config.businessSettings?.ccProcessingRate ?? defaultConfig.businessSettings.ccProcessingRate,
    getCcPerTransactionFee: () => config.businessSettings?.ccPerTransactionFee ?? defaultConfig.businessSettings.ccPerTransactionFee,

    // Square payment config helpers
    hasCustomSquareCredentials: () => config.squareConfig?.hasCustomCredentials || false,
    getSquareApplicationId: () => {
      if (config.squareConfig?.hasCustomCredentials && config.squareConfig?.applicationId) {
        return config.squareConfig.applicationId;
      }
      return process.env.REACT_APP_SQUARE_APPLICATION_ID;
    },
    getSquareLocationId: () => {
      if (config.squareConfig?.hasCustomCredentials && config.squareConfig?.locationId) {
        return config.squareConfig.locationId;
      }
      return process.env.REACT_APP_SQUARE_LOCATION_ID;
    },
    getSquareEnvironment: () => {
      if (config.squareConfig?.hasCustomCredentials && config.squareConfig?.environment) {
        return config.squareConfig.environment;
      }
      return process.env.REACT_APP_SQUARE_ENVIRONMENT || 'sandbox';
    },
    getSquareConfig: () => config.squareConfig || null,

    // Logo helpers
    getLogo: (type) => {
      const logos = config.branding?.logos || defaultConfig.branding.logos;
      // For light logo, fall back to full logo if not set
      if (type === 'light') {
        return logos.light || logos.full || defaultConfig.branding.logos.full;
      }
      return logos[type] || defaultConfig.branding.logos[type] || '/images/logo-full.png';
    },

    // Color helpers
    getColor: (colorName) => {
      return config.colors?.[colorName] || defaultConfig.colors[colorName];
    },
    getColors: () => config.colors || defaultConfig.colors,

    // Theme helper. Returns the tenant's configured starting theme for a
    // visitor with no stored choice. ThemeProvider applies it only in that
    // case, so this never overrides a real preference.
    getDefaultTheme: () => config.branding?.defaultTheme || defaultConfig.branding.defaultTheme,

    // ==================== TIER-BASED BRANDING ====================

    // Get current subscription tier
    getTier: () => currentTenant?.subscriptionTier || TIERS.FREE_TRIAL,

    // Tier checking helpers
    isTier: (tier) => (currentTenant?.subscriptionTier || TIERS.FREE_TRIAL) === tier,
    isFreeTrial: () => (currentTenant?.subscriptionTier || TIERS.FREE_TRIAL) === TIERS.FREE_TRIAL,
    isStarter: () => currentTenant?.subscriptionTier === TIERS.STARTER,
    isPro: () => currentTenant?.subscriptionTier === TIERS.PRO,
    isEnterprise: () => currentTenant?.subscriptionTier === TIERS.ENTERPRISE,
    isProOrHigher: () => [TIERS.PRO, TIERS.ENTERPRISE].includes(currentTenant?.subscriptionTier),

    // Check if logo upload is allowed for current tier
    canUploadLogo: () => {
      const tier = currentTenant?.subscriptionTier || TIERS.FREE_TRIAL;
      return [TIERS.PRO, TIERS.ENTERPRISE].includes(tier);
    },

    // Get logo upload restriction message
    getLogoUpgradeMessage: () => {
      const tier = currentTenant?.subscriptionTier || TIERS.FREE_TRIAL;
      if (tier === TIERS.FREE_TRIAL) {
        return `Upgrade to Starter (${formatPrice(monthlyPriceFor('starter'))}/mo) to customize your company name, or PRO (${formatPrice(monthlyPriceFor('pro'))}/mo) to upload a custom logo.`;
      }
      if (tier === TIERS.STARTER) {
        return `Upgrade to PRO (${formatPrice(monthlyPriceFor('pro'))}/mo) to upload a custom logo.`;
      }
      return null;
    },

    // Get the display logo based on tier and settings
    // Returns: { type: 'logo'|'text'|'deedstack', src?: string, lightSrc?: string, text?: string }
    getDisplayBranding: () => {
      const tier = currentTenant?.subscriptionTier || TIERS.FREE_TRIAL;
      const companyName = config.branding?.companyName || currentTenant?.companyName || 'Company Name';

      // FREE_TRIAL: Always show DeedStack logo
      if (tier === TIERS.FREE_TRIAL) {
        return {
          type: 'deedstack',
          src: DEEDSTACK_LOGOS.horizontal,
          lightSrc: DEEDSTACK_LOGOS.light
        };
      }

      // STARTER: Show company name as text (no logo)
      if (tier === TIERS.STARTER) {
        return {
          type: 'text',
          text: companyName,
          src: null,
          lightSrc: null
        };
      }

      // PRO/ENTERPRISE: Check if tenant has custom logo and display preference
      const logos = config.branding?.logos || {};
      const useTextLogo = config.branding?.useTextLogo === true;

      // Check if there's actually a valid custom logo URL (not a local default)
      // Custom logos are uploaded to Cloudinary (https://...) or external URLs
      // Local defaults start with '/images/'
      const hasCustomLogo = logos.full && !logos.full.startsWith('/images/');

      // If they prefer text OR don't have a custom logo uploaded
      if (useTextLogo || !hasCustomLogo) {
        return {
          type: 'text',
          text: companyName,
          src: null,
          lightSrc: null
        };
      }

      // They have a custom logo and want to display it
      return {
        type: 'logo',
        src: logos.full || logos.horizontal,
        lightSrc: logos.light || logos.full || logos.horizontal
      };
    },

    // Get footer branding configuration based on tier
    // Returns: { show: boolean, style: 'prominent'|'badge'|'small'|'none', text: string }
    getFooterBranding: () => {
      const tier = currentTenant?.subscriptionTier || TIERS.FREE_TRIAL;

      if (tier === TIERS.FREE_TRIAL) {
        return {
          show: true,
          style: 'prominent',
          text: 'Powered by DeedStack™',
          link: 'https://deedstack.com'
        };
      }

      if (tier === TIERS.STARTER) {
        return {
          show: true,
          style: 'badge',
          text: 'Powered by DeedStack™',
          link: 'https://deedstack.com'
        };
      }

      if (tier === TIERS.PRO) {
        return {
          show: true,
          style: 'small',
          text: 'Powered by DeedStack™',
          link: 'https://deedstack.com'
        };
      }

      // ENTERPRISE: No branding (full white-label)
      return {
        show: false,
        style: 'none',
        text: '',
        link: null
      };
    },

    // Tier constants for external use
    TIERS
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};

export default ConfigContext;
